import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, SlidersHorizontal, X, Compass, ArrowUpRight, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import PtfButton from "@/components/PtfButton";
import { Reveal } from "@/components/motion/Reveal";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { api, resolveImg } from "@/lib/api";
import { PROGRAMME_CATEGORIES, PROGRAMME_STATUSES, PROGRAMME_STATUS_LABEL, SDG_TITLE, IMAGES, locationLabel } from "@/lib/content";

const STATUS_STYLES = {
  CONCEPT: "bg-ptf-primary/10 text-ptf-primary",
  "IN DEVELOPMENT": "bg-ptf-secondary/15 text-ptf-secondary",
  PILOT: "bg-ptf-secondary/15 text-ptf-secondary",
  ACTIVE: "bg-ptf-accent text-ptf-primary",
  COMPLETED: "bg-ptf-primary text-ptf-bg",
  PAUSED: "bg-ptf-primary/8 text-ptf-text/60",
  ARCHIVED: "bg-ptf-primary/8 text-ptf-text/40",
};

function FilterPanel({ f, setF, categories, years, sdgs }) {
  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));
  const setBool = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.checked }));
  const active = Object.entries(f).some(([, v]) => (typeof v === "boolean" ? v : Boolean(v)));

  return (
    <div className="space-y-4" data-testid="programme-filters">
      <label className="block">
        <span className="text-[0.62rem] uppercase tracking-[0.16em] text-ptf-text/55">Programme area</span>
        <select data-testid="filter-category" value={f.category} onChange={set("category")} className="w-full border border-ptf-primary/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-ptf-secondary transition-colors text-ptf-text mt-1">
          <option value="">All areas</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="text-[0.62rem] uppercase tracking-[0.16em] text-ptf-text/55">Status</span>
        <select data-testid="filter-status" value={f.status} onChange={set("status")} className="w-full border border-ptf-primary/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-ptf-secondary transition-colors text-ptf-text mt-1">
          <option value="">All statuses</option>
          {PROGRAMME_STATUSES.map((s) => <option key={s} value={s}>{PROGRAMME_STATUS_LABEL[s] || s}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="text-[0.62rem] uppercase tracking-[0.16em] text-ptf-text/55">India / World</span>
        <select data-testid="filter-scope" value={f.scope} onChange={set("scope")} className="w-full border border-ptf-primary/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-ptf-secondary transition-colors text-ptf-text mt-1">
          <option value="">India &amp; World</option>
          <option value="INDIA">India</option>
          <option value="WORLD">World</option>
        </select>
      </label>
      {years.length > 0 && (
        <label className="block">
          <span className="text-[0.62rem] uppercase tracking-[0.16em] text-ptf-text/55">Year</span>
          <select data-testid="filter-year" value={f.year} onChange={set("year")} className="w-full border border-ptf-primary/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-ptf-secondary transition-colors text-ptf-text mt-1">
            <option value="">All years</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
      )}
      {sdgs.length > 0 && (
        <label className="block">
          <span className="text-[0.62rem] uppercase tracking-[0.16em] text-ptf-text/55">SDG</span>
          <select data-testid="filter-sdg" value={f.sdg} onChange={set("sdg")} className="w-full border border-ptf-primary/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-ptf-secondary transition-colors text-ptf-text mt-1">
            <option value="">All SDGs</option>
            {sdgs.map((n) => <option key={n} value={n}>SDG {n}: {SDG_TITLE[n] || ""}</option>)}
          </select>
        </label>
      )}
      <div className="space-y-2.5 pt-2">
        <label className="flex items-center gap-2.5 text-sm text-ptf-text/75">
          <input type="checkbox" data-testid="filter-training" checked={f.training} onChange={setBool("training")} className="h-4 w-4 accent-ptf-secondary" />
          Training available
        </label>
        <label className="flex items-center gap-2.5 text-sm text-ptf-text/75">
          <input type="checkbox" data-testid="filter-volunteer" checked={f.volunteer} onChange={setBool("volunteer")} className="h-4 w-4 accent-ptf-secondary" />
          Volunteer opportunity
        </label>
        <label className="flex items-center gap-2.5 text-sm text-ptf-text/75">
          <input type="checkbox" data-testid="filter-partnership" checked={f.partnership} onChange={setBool("partnership")} className="h-4 w-4 accent-ptf-secondary" />
          Partnership opportunity
        </label>
      </div>
      {active && (
        <button
          type="button"
          data-testid="filters-clear"
          onClick={() => setF({ category: "", status: "", scope: "", year: "", sdg: "", training: false, volunteer: false, partnership: false })}
          className="inline-flex items-center gap-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-ptf-secondary hover:text-ptf-primary"
        >
          <X className="h-3.5 w-3.5" /> Clear filters
        </button>
      )}
    </div>
  );
}

export default function Programmes() {
  const { data: programmes = [], isLoading } = useQuery({
    queryKey: ["programmes"],
    queryFn: async () => (await api.get("/programmes")).data,
  });
  const [q, setQ] = useState("");
  const [f, setF] = useState({ category: "", status: "", scope: "", year: "", sdg: "", training: false, volunteer: false, partnership: false });

  const categories = useMemo(
    () => Array.from(new Set([...PROGRAMME_CATEGORIES, ...programmes.map((p) => p.category).filter(Boolean)])),
    [programmes]
  );
  const years = useMemo(() => [...new Set(programmes.map((p) => p.year).filter(Boolean))].sort((a, b) => b - a), [programmes]);
  const sdgs = useMemo(() => [...new Set(programmes.flatMap((p) => p.sdgs || []))].sort((a, b) => a - b), [programmes]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return programmes.filter((p) => {
      if (f.category && p.category !== f.category) return false;
      if (f.status && p.status !== f.status) return false;
      if (f.scope && (p.scope || "INDIA") !== f.scope) return false;
      if (f.year && String(p.year) !== String(f.year)) return false;
      if (f.sdg && !(p.sdgs || []).includes(Number(f.sdg))) return false;
      if (f.training && !(p.related_training_categories?.length > 0)) return false;
      if (f.volunteer && !(p.related_volunteer_categories?.length > 0 || p.volunteer_component)) return false;
      if (f.partnership && !(p.partnership_requirements?.length > 0)) return false;
      if (needle && !`${p.name} ${p.category} ${p.short_description || ""} ${p.objective || ""}`.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [programmes, q, f]);

  return (
    <>
      <Seo title="Programmes" path="/programmes" description="Planet Trotter Foundation's programme portfolio across conservation, agriculture, digital inclusion, education, livelihoods, tourism, water, waste, disaster resilience and health awareness. Each programme carries an honest status: concept, in development, pilot, active, completed, paused or archived." />
      <PageHero
        eyebrow="Programmes"
        titleLines={["Work we", "undertake."]}
        intro="Impact-oriented initiatives across conservation, communities, education, livelihoods and more. Each programme is documented here with an honest status, from concept to completion."
        image={IMAGES.forest}
      />

      <section className="bg-ptf-bg py-12 md:py-16">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row gap-3 md:items-center mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ptf-text/40" />
              <input
                data-testid="programme-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search programmes"
                className="w-full bg-white border border-ptf-primary/20 pl-11 pr-4 py-3.5 text-sm outline-none focus:border-ptf-secondary transition-colors text-ptf-text placeholder:text-ptf-text/40"
              />
            </div>
            <div className="lg:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <button data-testid="mobile-filters-open" className="inline-flex items-center gap-2 border border-ptf-primary/25 bg-white px-5 py-3.5 text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-ptf-primary w-full justify-center">
                    <SlidersHorizontal className="h-4 w-4" /> Filters
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="bg-ptf-bg overflow-y-auto w-[320px]">
                  <SheetTitle className="font-display uppercase tracking-tight text-ptf-primary mb-5">Filter programmes</SheetTitle>
                  <FilterPanel f={f} setF={setF} categories={categories} years={years} sdgs={sdgs} />
                </SheetContent>
              </Sheet>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <aside className="hidden lg:block lg:col-span-3 bg-white border border-ptf-primary/12 p-6 sticky top-28">
              <FilterPanel f={f} setF={setF} categories={categories} years={years} sdgs={sdgs} />
            </aside>
            <div className="lg:col-span-9">
              <p data-testid="results-count" className="text-[0.68rem] uppercase tracking-[0.18em] text-ptf-text/55 mb-5">
                {isLoading ? "Loading programmes" : `${filtered.length} programme${filtered.length === 1 ? "" : "s"}`}
              </p>

              {!isLoading && filtered.length === 0 ? (
                <Reveal>
                  <div data-testid="programmes-empty" className="border border-dashed border-ptf-primary/25 py-24 px-8 text-center">
                    <span className="grid place-items-center h-16 w-16 rounded-full bg-ptf-primary/5 mx-auto mb-6">
                      <Compass className="h-7 w-7 text-ptf-secondary" />
                    </span>
                    <h3 className="font-display uppercase text-3xl md:text-4xl tracking-tight text-ptf-primary">No programmes match.</h3>
                    <p className="mt-4 max-w-lg mx-auto text-ptf-text/70 leading-relaxed">
                      Try clearing a filter, or check back as more programmes move from design to the field.
                    </p>
                    <div className="mt-8 flex justify-center">
                      <PtfButton to="/partnerships" variant="outline">Help Shape a Programme</PtfButton>
                    </div>
                  </div>
                </Reveal>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filtered.map((p, i) => (
                    <Reveal key={p.id} delay={(i % 6) * 0.04}>
                      <Link to={`/programmes/${p.slug || p.id}`} data-testid={`programme-card-${p.id}`} className="group h-full flex flex-col border border-ptf-primary/12 overflow-hidden hover:border-ptf-primary/30 transition-colors">
                        {p.images?.[0] && (
                          <div className="aspect-[16/10] overflow-hidden">
                            <img src={resolveImg(p.images[0])} alt={p.name} className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" loading="lazy" />
                          </div>
                        )}
                        <div className="p-6 flex flex-col flex-1">
                          <div className="flex items-center gap-2 mb-3 flex-wrap">
                            <span className={`text-[0.58rem] uppercase tracking-[0.14em] px-2.5 py-1 ${STATUS_STYLES[p.status] || STATUS_STYLES.CONCEPT}`}>{p.status}</span>
                            <span className="text-[0.58rem] uppercase tracking-[0.14em] text-ptf-text/50">{p.category}</span>
                          </div>
                          <h3 className="font-display uppercase text-xl tracking-tight text-ptf-primary leading-tight group-hover:text-ptf-secondary transition-colors">{p.name}</h3>
                          {(p.location || p.region) && <p className="mt-2 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] text-ptf-secondary"><MapPin className="h-3.5 w-3.5" />{locationLabel(p) || p.location}</p>}
                          {(p.short_description || p.objective) && <div className="mt-3 flex-1"><p className="text-sm text-ptf-text/70 leading-relaxed line-clamp-3">{p.short_description || p.objective}</p></div>}
                          <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-ptf-primary">View Programme <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
                        </div>
                      </Link>
                    </Reveal>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border-t border-ptf-primary/10 py-14 md:py-16">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link to="/where-we-work" data-testid="programmes-to-map" className="group flex items-center justify-between gap-4 border border-ptf-primary/15 p-7 hover:border-ptf-primary/40 transition-colors">
            <div>
              <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-secondary">Where We Work</p>
              <p className="mt-2 font-display uppercase text-2xl tracking-tight text-ptf-primary">See the map of published work.</p>
            </div>
            <ArrowUpRight className="h-6 w-6 shrink-0 text-ptf-primary transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <Link to="/partnerships" data-testid="programmes-to-partnerships" className="group flex items-center justify-between gap-4 border border-ptf-primary/15 p-7 hover:border-ptf-primary/40 transition-colors">
            <div>
              <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-secondary">Partnerships</p>
              <p className="mt-2 font-display uppercase text-2xl tracking-tight text-ptf-primary">Help fund or field the next programme.</p>
            </div>
            <ArrowUpRight className="h-6 w-6 shrink-0 text-ptf-primary transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </section>
    </>
  );
}
